import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, timer } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

/**
 * How long after a navigation the remaining lazy pages start downloading.
 * Card #356: long enough for the dashboard's own requests to finish first.
 */
export const PRELOAD_DELAY_MS = 10000;

/**
 * Card #356: PreloadAllModules, but later.
 *
 * With PreloadAllModules, the moment the dashboard route finished loading the app started
 * downloading every other lazy page (about 30 files; 34 MB on dev, which is served
 * uncompressed and unminified) while the dashboard was still making its own requests. On a
 * first visit on dev those requests took 1.4-1.9 s each instead of 0.2-0.5 s, and parsing the
 * extra code took several seconds of main-thread time. The pages are still preloaded, so later
 * navigation stays instant; they just wait their turn. A page the user opens before then is
 * loaded on demand, as with any lazy route (the router shares one load per route, so nothing
 * downloads twice).
 *
 * The router asks for preloads after each NavigationEnd, and only for routes whose parent is
 * already loaded. The dashboard's sibling pages live under the dash layout, so they are first
 * offered at the dashboard's own NavigationEnd (measured on dev: the burst began 1.8 s after the
 * dashboard route, never on the sign-in page), which is what the delay counts from.
 */
@Injectable({ providedIn: 'root' })
export class DelayedPreloadingStrategy implements PreloadingStrategy {
  preload(_route: Route, load: () => Observable<any>): Observable<any> {
    return timer(PRELOAD_DELAY_MS).pipe(mergeMap(() => load()));
  }
}
